import { useEffect } from 'react';
import { getCadPlanBackgroundColor } from '../../Map/styles/mapTheme';

/** Loteo + ilustración: el resto es ruido mientras se alinea. */
export function isCadAlignKeepLayerId(layerId) {
  return String(layerId || '').startsWith('cad-');
}

function readVisibility(map, layerId) {
  try {
    return map.getLayoutProperty(layerId, 'visibility') || 'visible';
  } catch (_) {
    return 'visible';
  }
}

/**
 * Apaga el mapa país (satélite, catastro, 3D, labels) mientras se alinea
 * la ilustración. Quedan el loteo y el render sobre el fondo del plano.
 *
 * @param {{
 *   mapInstanceRef: { current: import('maplibre-gl').Map|null },
 *   enabled?: boolean,
 * }} options
 */
export function useCadAlignQuietMap({ mapInstanceRef, enabled = false }) {
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled) return undefined;

    const savedVisibility = new Map();
    const savedBackground = new Map();
    const color = getCadPlanBackgroundColor('dark');
    let canvasBg = '';
    let containerBg = '';

    try {
      canvasBg = map.getCanvas().style.backgroundColor;
      map.getCanvas().style.backgroundColor = color;
    } catch (_) { /* noop */ }
    try {
      const container = map.getContainer();
      containerBg = container.style.backgroundColor;
      container.style.backgroundColor = color;
    } catch (_) { /* noop */ }

    const apply = () => {
      const layers = map.getStyle?.()?.layers;
      if (!Array.isArray(layers)) return;
      layers.forEach((layer) => {
        const id = layer?.id;
        if (!id) return;
        if (layer.type === 'background') {
          if (!savedBackground.has(id)) {
            try {
              savedBackground.set(id, map.getPaintProperty(id, 'background-color'));
            } catch (_) {
              savedBackground.set(id, null);
            }
          }
          try { map.setPaintProperty(id, 'background-color', color); } catch (_) { /* noop */ }
          return;
        }
        if (isCadAlignKeepLayerId(id)) return;
        if (!savedVisibility.has(id)) savedVisibility.set(id, readVisibility(map, id));
        try {
          if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
        } catch (_) { /* estilo a medias */ }
      });
    };

    const onStyleData = () => {
      if (!map.isStyleLoaded()) return;
      apply();
    };

    apply();
    map.on('styledata', onStyleData);

    return () => {
      try { map.off('styledata', onStyleData); } catch (_) { /* noop */ }
      savedVisibility.forEach((visibility, id) => {
        try {
          if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility);
        } catch (_) { /* estilo a medias */ }
      });
      savedBackground.forEach((paint, id) => {
        if (paint == null) return;
        try {
          if (map.getLayer(id)) map.setPaintProperty(id, 'background-color', paint);
        } catch (_) { /* estilo a medias */ }
      });
      try { map.getCanvas().style.backgroundColor = canvasBg; } catch (_) { /* noop */ }
      try { map.getContainer().style.backgroundColor = containerBg; } catch (_) { /* noop */ }
    };
  }, [enabled, mapInstanceRef]);
}
